/**
 * Client-side mirror of `internal/sitecopy/validate.go`.
 *
 * The server is the authority — every rule here is enforced again on `PUT` and
 * a third time at publish. This module exists so an editor sees the problem
 * next to the field they broke instead of as a red toast after a round trip,
 * and so a save that cannot succeed never leaves the browser.
 *
 * Rules, in the backend's order: shape → structural → markup → placeholders →
 * length → §19 carve-outs. Keeping the order identical matters: a caller must
 * not get a length complaint about a value that is the wrong shape entirely.
 */

import {
  copyValueForShape,
  type CopyItem,
  type CopyItemField,
  type CopyShape,
  type CopyValue,
} from '@/schemas/admin/copy';
import { scanContent, type GuardHit } from './contentGuard';

/** Hard caps from `validate.go`. Rune-counted — a byte cap halves Arabic. */
export const MAX_STRING_RUNES = 2000;
export const MAX_LIST_ITEMS = 50;

/** Matches the backend's `placeholderRe`. */
const PLACEHOLDER_RE = /\{([A-Za-z0-9_]+)\}/g;

/** Matches the backend's `markupPatterns`. */
const MARKUP_PATTERNS = [
  '<script',
  '</script',
  '<iframe',
  '<object',
  '<embed',
  '<svg',
  'javascript:',
  'data:text/html',
  'srcdoc=',
] as const;

/** Matches the backend's `eventHandlerRe`. */
const EVENT_HANDLER_RE = /\bon[a-z]{3,}\s*=\s*["'a-z(]/i;

export type CopyIssueCode =
  | 'invalid_shape'
  | 'empty_value'
  | 'structural_field_locked'
  | 'unclassified_field'
  | 'markup_rejected'
  | 'placeholder_missing'
  | 'placeholder_unknown'
  | 'value_too_long'
  | 'list_too_long'
  | 'carveout_violation'
  | 'carveout_review'
  | 'retired_framing'
  | 'length_doubled';

export interface CopyIssue {
  code: CopyIssueCode;
  /** Leaf path inside the value, e.g. `[2].body` — `''` for a plain string. */
  field: string;
  message: string;
  /** Placeholder token / banned term / limit the message is about. */
  token?: string;
}

export interface CopyValidationResult {
  errors: CopyIssue[];
  warnings: CopyIssue[];
  /** Convenience for the save button. */
  ok: boolean;
}

export interface CopyValidationInput {
  shape: CopyShape;
  itemFields: readonly CopyItemField[];
  maxLen: number | null;
  /** The shipped baseline for this locale — every rule compares against it. */
  baseline: CopyValue | null | undefined;
  value: CopyValue;
}

/** Count runes (code points), not UTF-16 units — matches Go's rune count. */
export function runeLength(s: string): number {
  return [...s].length;
}

export function placeholderSet(s: string): Set<string> {
  const out = new Set<string>();
  for (const m of s.matchAll(PLACEHOLDER_RE)) {
    if (m[1]) out.add(m[1]);
  }
  return out;
}

/**
 * §19 carve-outs are matched *relative to the baseline*: the site's own
 * anti-carve-out prose legitimately names what it bans, so a hard-blocked term
 * already present in the reviewed baseline may be reworded around. Only a term
 * the override *introduces* is a violation.
 */
export function scanAgainstBaseline(baseline: string, value: string): GuardHit[] {
  const inBaseline = new Set(scanContent(baseline).map((h) => `${h.severity}:${h.term}`));
  return scanContent(value).filter((h) => !inBaseline.has(`${h.severity}:${h.term}`));
}

/* -------------------------------------------------------------------------- */
/* Leaf walking                                                                */
/* -------------------------------------------------------------------------- */

interface Leaf {
  path: string;
  base: string;
  got: string;
}

/**
 * Pair every string leaf of `value` with the matching baseline leaf. Only
 * called after the shape check has proved the two trees line up, so a missing
 * baseline leaf here means the value is structurally novel and is reported as
 * a shape problem by the caller.
 */
function leaves(shape: CopyShape, baseline: CopyValue | null | undefined, value: CopyValue): Leaf[] {
  if (shape === 'string') {
    return [
      {
        path: '',
        base: typeof baseline === 'string' ? baseline : '',
        got: typeof value === 'string' ? value : '',
      },
    ];
  }
  if (shape === 'string_list') {
    const base = Array.isArray(baseline) ? (baseline as string[]) : [];
    return (value as string[]).map((got, i) => {
      const b = base[i];
      return { path: `[${i}]`, base: typeof b === 'string' ? b : '', got };
    });
  }
  const base = Array.isArray(baseline) ? (baseline as CopyItem[]) : [];
  const out: Leaf[] = [];
  (value as CopyItem[]).forEach((item, i) => {
    for (const [name, got] of Object.entries(item)) {
      const b = base[i]?.[name];
      if (Array.isArray(got)) {
        // A list-valued field (`cities`) — one leaf per entry, addressed
        // `[i].name[j]` exactly as the backend's walkLeaves reports it.
        got.forEach((entry, j) => {
          const be = Array.isArray(b) ? b[j] : undefined;
          out.push({ path: `[${i}].${name}[${j}]`, base: be ?? '', got: entry });
        });
        continue;
      }
      out.push({ path: `[${i}].${name}`, base: typeof b === 'string' ? b : '', got });
    }
  });
  return out;
}

/** Mirrors `jsonKind` in validate.go for the two kinds an item field may take. */
function fieldKind(v: unknown): 'string' | 'list' | 'other' {
  if (typeof v === 'string') return 'string';
  return Array.isArray(v) ? 'list' : 'other';
}

function issue(code: CopyIssueCode, field: string, message: string, token?: string): CopyIssue {
  return token === undefined ? { code, field, message } : { code, field, message, token };
}

/* -------------------------------------------------------------------------- */
/* Rules                                                                       */
/* -------------------------------------------------------------------------- */

function validateShape(input: CopyValidationInput): CopyIssue[] {
  const { shape, baseline, value } = input;
  if (!copyValueForShape(shape).safeParse(value).success) {
    return [issue('invalid_shape', '', `value is not a valid ${shape}`)];
  }
  if (baseline == null) return [];

  if (shape === 'string_list') {
    const base = baseline as string[];
    const got = value as string[];
    if (!Array.isArray(base)) return [];
    if (got.length !== base.length) {
      return [
        issue(
          'invalid_shape',
          '',
          `this list is locked to ${base.length} items; the override has ${got.length}`
        ),
      ];
    }
    return [];
  }

  if (shape === 'object_list') {
    const base = baseline as CopyItem[];
    const got = value as CopyItem[];
    if (!Array.isArray(base)) return [];
    if (got.length !== base.length) {
      return [
        issue(
          'invalid_shape',
          '',
          `this list is locked to ${base.length} items; the override has ${got.length}`
        ),
      ];
    }
    const out: CopyIssue[] = [];
    got.forEach((item, i) => {
      const baseItem = base[i] ?? {};
      const baseKeys = Object.keys(baseItem).sort();
      const gotKeys = Object.keys(item).sort();
      if (baseKeys.join('\u0000') !== gotKeys.join('\u0000')) {
        out.push(
          issue('invalid_shape', `[${i}]`, `item ${i + 1} must keep the fields ${baseKeys.join(', ')}`)
        );
        return;
      }
      // sameLeafKind: a field keeps the baseline's kind, and a list its length.
      for (const name of baseKeys) {
        const b = baseItem[name];
        const g = item[name];
        if (fieldKind(b) !== fieldKind(g)) {
          out.push(
            issue('invalid_shape', `[${i}].${name}`, `“${name}” must stay a ${fieldKind(b)}`)
          );
        } else if (Array.isArray(b) && Array.isArray(g) && b.length !== g.length) {
          out.push(
            issue(
              'invalid_shape',
              `[${i}].${name}`,
              `“${name}” is locked to ${b.length} entries; the override has ${g.length}`
            )
          );
        }
      }
    });
    return out;
  }

  return [];
}

function validateEmpty(input: CopyValidationInput): CopyIssue[] {
  const { shape, value } = input;
  const blank = 'an empty value would render nothing — revert to the baseline instead';
  if (shape === 'string') {
    return typeof value === 'string' && value.trim() === '' ? [issue('empty_value', '', blank)] : [];
  }
  const out: CopyIssue[] = [];
  if (shape === 'string_list') {
    (value as string[]).forEach((s, i) => {
      if (s.trim() === '') out.push(issue('empty_value', `[${i}]`, blank));
    });
    return out;
  }
  (value as CopyItem[]).forEach((item, i) => {
    for (const [name, v] of Object.entries(item)) {
      if (Array.isArray(v)) {
        v.forEach((entry, j) => {
          if (entry.trim() === '') out.push(issue('empty_value', `[${i}].${name}[${j}]`, blank));
        });
      } else if (v.trim() === '') {
        out.push(issue('empty_value', `[${i}].${name}`, blank));
      }
    }
  });
  return out;
}

function validateStructural(input: CopyValidationInput): CopyIssue[] {
  const { shape, itemFields, baseline, value } = input;
  if (shape !== 'object_list' || baseline == null) return [];
  const base = baseline as CopyItem[];
  const got = value as CopyItem[];
  const roles = new Map(itemFields.map((f) => [f.name, f.role]));
  const out: CopyIssue[] = [];
  got.forEach((item, i) => {
    const baseItem = base[i];
    if (!baseItem) return;
    for (const [name, v] of Object.entries(item)) {
      const role = roles.get(name);
      if (role === undefined) {
        // Fails closed, exactly like the backend's `unclassified_field`.
        out.push(
          issue(
            'unclassified_field',
            `[${i}].${name}`,
            `“${name}” has no classification in the catalog — it cannot be edited here`
          )
        );
        continue;
      }
      if (role === 'structural' && JSON.stringify(v) !== JSON.stringify(baseItem[name])) {
        out.push(
          issue(
            'structural_field_locked',
            `[${i}].${name}`,
            `“${name}” is a structural field — the site indexes it, so it cannot be changed`,
            name
          )
        );
      }
    }
  });
  return out;
}

function validateMarkup(all: Leaf[]): CopyIssue[] {
  const out: CopyIssue[] = [];
  for (const leaf of all) {
    const low = leaf.got.toLowerCase();
    const hit = MARKUP_PATTERNS.find((p) => low.includes(p));
    if (hit) {
      out.push(
        issue('markup_rejected', leaf.path, `site copy is plain text; “${hit}” is not allowed`, hit)
      );
      continue;
    }
    if (EVENT_HANDLER_RE.test(low)) {
      out.push(
        issue(
          'markup_rejected',
          leaf.path,
          'site copy is plain text; inline event handlers are not allowed'
        )
      );
    }
  }
  return out;
}

function validatePlaceholders(all: Leaf[]): CopyIssue[] {
  const out: CopyIssue[] = [];
  for (const leaf of all) {
    const want = placeholderSet(leaf.base);
    const got = placeholderSet(leaf.got);
    for (const tok of want) {
      if (!got.has(tok)) {
        out.push(
          issue('placeholder_missing', leaf.path, `the value must keep the {${tok}} placeholder`, tok)
        );
      }
    }
    for (const tok of got) {
      if (!want.has(tok)) {
        out.push(
          issue(
            'placeholder_unknown',
            leaf.path,
            `{${tok}} is not a placeholder this string supports`,
            tok
          )
        );
      }
    }
  }
  return out;
}

function validateLength(
  input: CopyValidationInput,
  all: Leaf[]
): { errors: CopyIssue[]; warnings: CopyIssue[] } {
  const limit =
    input.maxLen != null && input.maxLen > 0 && input.maxLen < MAX_STRING_RUNES
      ? input.maxLen
      : MAX_STRING_RUNES;
  const errors: CopyIssue[] = [];
  const warnings: CopyIssue[] = [];

  if (Array.isArray(input.value) && input.value.length > MAX_LIST_ITEMS) {
    errors.push(
      issue('list_too_long', '', `a copy list may hold at most ${MAX_LIST_ITEMS} items`, String(MAX_LIST_ITEMS))
    );
  }
  for (const leaf of all) {
    const len = runeLength(leaf.got);
    if (len > limit) {
      errors.push(
        issue('value_too_long', leaf.path, `${len} characters — the limit is ${limit}`, String(limit))
      );
      continue;
    }
    const baseLen = runeLength(leaf.base);
    if (baseLen > 0 && len > baseLen * 2) {
      warnings.push(
        issue(
          'length_doubled',
          leaf.path,
          `${len} characters is more than twice the baseline (${baseLen}) — check the layout after publishing`,
          String(baseLen)
        )
      );
    }
  }
  return { errors, warnings };
}

function validateCarveouts(all: Leaf[]): { errors: CopyIssue[]; warnings: CopyIssue[] } {
  const errors: CopyIssue[] = [];
  const warnings: CopyIssue[] = [];
  for (const leaf of all) {
    for (const hit of scanAgainstBaseline(leaf.base, leaf.got)) {
      if (hit.severity === 'banned') {
        errors.push(
          issue(
            'carveout_violation',
            leaf.path,
            `introduces the banned §19 term “${hit.term}” — this copy cannot ship`,
            hit.term
          )
        );
      } else {
        warnings.push(
          issue(
            'retired_framing',
            leaf.path,
            `“${hit.term}” is a retired framing — reword it unless you mean it`,
            hit.term
          )
        );
      }
    }
  }
  return { errors, warnings };
}

/* -------------------------------------------------------------------------- */
/* Entry point                                                                 */
/* -------------------------------------------------------------------------- */

export function validateCopyValue(input: CopyValidationInput): CopyValidationResult {
  const shapeErrors = validateShape(input);
  if (shapeErrors.length > 0) return { errors: shapeErrors, warnings: [], ok: false };

  const emptyErrors = validateEmpty(input);
  const structuralErrors = validateStructural(input);
  const all = leaves(input.shape, input.baseline, input.value);
  const markupErrors = validateMarkup(all);
  const placeholderErrors = validatePlaceholders(all);
  const length = validateLength(input, all);
  const carve = validateCarveouts(all);

  const errors = [
    ...emptyErrors,
    ...structuralErrors,
    ...markupErrors,
    ...placeholderErrors,
    ...length.errors,
    ...carve.errors,
  ];
  const warnings = [...length.warnings, ...carve.warnings];
  return { errors, warnings, ok: errors.length === 0 };
}

/** True when the two values differ — used to decide which locales to PUT. */
export function copyValueChanged(a: CopyValue | null | undefined, b: CopyValue | null | undefined) {
  return JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
}
